import { List } from "immutable";
import moment from "moment";

import {
  SpecTypes,
  VOCABULARY_SPEC_TYPE
} from "@seneca/assignments-service-consts";

import { StudentAssignment } from "seneca-common/features/assignments/features/student-assignments/state";
import { getAssignmentQuizDetails } from "seneca-common/features/assignments/features/student-assignments/state/selectors/state/assignments/assignment/studentAssignment";
import CourseExamQuestionsSpec, {
  COURSE_EXAM_QUESTIONS_SPEC_TYPE
} from "seneca-common/features/assignments/state/models/spec/CourseExamQuestionsSpec";
import QuizzesOnlySpec, {
  QUIZZES_ONLY_SPEC_TYPE
} from "seneca-common/features/assignments/state/models/spec/QuizzesOnlySpec";
import VocabularySpec from "seneca-common/features/assignments/state/models/spec/VocabularySpec";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import Assignment, {
  ReceivedAssignmentStatus
} from "../../../../models/Assignment";
import CourseSectionsSpec, {
  COURSE_SECTIONS_SPEC_TYPE
} from "../../../../models/spec/CourseSectionsSpec";
import { COURSE_USER_SECTIONS_SPEC_TYPE } from "../../../../models/spec/CourseUserSectionsSpec";
import {
  getContentIds,
  getContentIdsForSection,
  getExamQuestionIds,
  getSectionIds
} from "./spec/courseSectionsSpec";
import {
  getCourseId as courseUserSectionsCourseId,
  getAllSectionIds,
  getUserSectionIds as getUserSectionIdsSelector
} from "./spec/courseUserSectionsSpec";
import { getQuizIds } from "./spec/quizzesOnlySpec";
import { getWordIds } from "./spec/vocabularySpec";

export function getAssignmentId(assignment: Assignment) {
  return assignment.id!;
}

// @ts-ignore
export const getAssignmentCourseId: (arg0: Assignment) => string =
  composeSelector(getSpec as any, courseUserSectionsCourseId);

export const getAssignmentQuizTitle = (
  assignment: Assignment
): string | null => {
  const specType = getSpecType(assignment);
  if (specType !== SpecTypes.quizzes) {
    return null;
  }

  const quizDetails = getAssignmentQuizDetails(assignment);
  const quizIds = getAssignmentQuizIds(assignment);
  return quizDetails?.get(quizIds.first())?.title || null;
};

export function getAssignmentSectionIds(assignment: Assignment): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);
  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getSectionIds(spec as any);
    case COURSE_USER_SECTIONS_SPEC_TYPE:
      return getAllSectionIds(spec as any);
    default:
      return List();
  }
}

export function getAssignmentContentIds(assignment: Assignment): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);

  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getContentIds(spec as CourseSectionsSpec);
    case COURSE_USER_SECTIONS_SPEC_TYPE:
    default:
      return List();
  }
}

export function getAssignmentContentIdsForSection(
  assignment: Assignment,
  sectionId: string
): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);

  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getContentIdsForSection(spec as CourseSectionsSpec, sectionId);
    case COURSE_USER_SECTIONS_SPEC_TYPE:
    default:
      return List();
  }
}

export function getAssignmentExamQuestionIds(
  assignment: Assignment
): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);

  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getExamQuestionIds(spec as CourseSectionsSpec);
    case COURSE_EXAM_QUESTIONS_SPEC_TYPE:
      return getExamQuestionIds(spec as CourseExamQuestionsSpec);
    default:
      return List();
  }
}

export function getAssignmentQuizIds(assignment: Assignment): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);

  if (specType === QUIZZES_ONLY_SPEC_TYPE) {
    return getQuizIds(spec as QuizzesOnlySpec);
  }

  return List();
}

export function getAssignmentWordIds(assignment: Assignment): List<string> {
  const specType = getSpecType(assignment);
  const spec = getSpec(assignment);

  if (specType === VOCABULARY_SPEC_TYPE) {
    return getWordIds(spec as VocabularySpec);
  }

  return List();
}

export function assignmentIncludesExamQuestions(assignment: Assignment) {
  return getAssignmentExamQuestionIds(assignment).size > 0;
}

export function assignmentHasNoSections(assignment: Assignment) {
  return getAssignmentSectionIds(assignment).size === 0;
}

export function assignmentIncludesQuizzes(assignment: Assignment) {
  return getAssignmentQuizIds(assignment).size > 0;
}

export const getUserSectionIds: (
  assignment: Assignment,
  userId: string
) => List<string> = composeSelector(getSpec as any, getUserSectionIdsSelector);

export function getAssignmentNumberOfSections(assignment: Assignment) {
  const contentTypesBySectionId = (assignment as StudentAssignment)
    .contentTypesBySectionId;

  const sectionContentsSize =
    contentTypesBySectionId?.valueSeq().flatten().toList().size ?? 0;

  if (sectionContentsSize > 0) return sectionContentsSize;

  return getAssignmentSectionIds(assignment).size;
}

export function getAssignmentNumberOfExamQuestions(assignment: Assignment) {
  return getAssignmentExamQuestionIds(assignment).size;
}

export function getClassId(assignment: Assignment) {
  return assignment.classId;
}

export function getStartDate(assignment?: Assignment) {
  return assignment?.startDate;
}

export function getDueDate(assignment: Assignment) {
  return assignment.dueDate;
}

export function getStatus(assignment: Assignment): ReceivedAssignmentStatus {
  return assignment.status!;
}

export function isStatusComplete(assignment: Assignment): boolean {
  return getStatus(assignment) === ReceivedAssignmentStatus.complete;
}

export function isStatusLate(assignment: Assignment): boolean {
  return getStatus(assignment) === ReceivedAssignmentStatus.late;
}

export function isStatusIncomplete(assignment: Assignment): boolean {
  return getStatus(assignment) === ReceivedAssignmentStatus.incomplete;
}

export function getName(assignment: Assignment) {
  return assignment.name;
}

export function getNumAssignees(assignment: Assignment) {
  return assignment.numAssignees;
}

export function getSpecType(assignment: Assignment) {
  return assignment?.specType;
}

export function isSmart(assignment: Assignment): boolean {
  return getSpecType(assignment) === COURSE_USER_SECTIONS_SPEC_TYPE;
}

export function isExamQuestionsOnly(assignment: Assignment): boolean {
  return getSpecType(assignment) === SpecTypes.courseExamQuestions;
}

export function isQuizzesOnly(assignment: Assignment): boolean {
  return getSpecType(assignment) === SpecTypes.quizzes;
}

export function getSpec(assignment: Assignment) {
  return assignment.spec;
}

export function getAssignmentCutoffDate(assignment: Assignment) {
  return assignment.assignmentCutoffDate;
}

export function isFutureAssignment(
  assignment: Assignment,
  now: moment.Moment
): boolean {
  return (
    now.isBefore(getDueDate(assignment)) &&
    now.isAfter(getStartDate(assignment))
  );
}

export function isPastAssignment(
  assignment: Assignment,
  now: moment.Moment
): boolean {
  return now.isAfter(getDueDate(assignment));
}

export function isAssignmentAfterCutoffDate(
  assignment: Assignment,
  now: moment.Moment
): boolean {
  return now.isAfter(getAssignmentCutoffDate(assignment));
}
